import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const Logo = ({ dark = false, siteLogo }) => (
  <Link to="/" className="flex items-center gap-2 select-none">
    {siteLogo ? (
      <img src={siteLogo} alt="TicketsPro.app" className="h-[72px] max-h-[72px] w-auto object-contain" />
    ) : (
      <span className="text-[2.6rem] font-semibold tracking-tight" style={{ color: dark ? "#16243b" : "#ffffff" }}>
        TicketsPro.app
      </span>
    )}
  </Link>
);

export default function Header({ content = {} }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = content.header_links || [];
  const dark = scrolled;
  const ctaText = content.header_cta || "Get started";
  const ctaLink = content.header_cta_link || "/signup";
  const ctaExternal = /^https?:\/\//i.test(ctaLink);
  const Cta = ({ className }) => ctaExternal
    ? <a href={ctaLink} data-testid="header-cta" className={className}>{ctaText}</a>
    : <Link to={ctaLink} data-testid="header-cta" className={className}>{ctaText}</Link>;
  const textColor = dark ? "text-[#16243b]" : "text-white";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white shadow-[0_4px_20px_rgba(0,0,0,0.06)]" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1240px] mx-auto px-6 h-[76px] flex items-center justify-between">
        <Logo dark={dark} siteLogo={content.site_logo} />

        <nav className="hidden lg:flex items-center gap-8">
          <Link to="/events" data-testid="nav-browse-events" className={`text-[0.95rem] font-medium ${textColor} hover:opacity-70 transition-opacity`}>
            Browse events
          </Link>
          {links.map((link, i) => (
            <a key={i} href={link.url || "#"}
              className={`text-[0.95rem] font-medium ${textColor} hover:opacity-70 transition-opacity`}>
              {link.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-6">
          <Link to="/check-balance" className={`text-[0.95rem] font-medium ${textColor} hover:opacity-70 transition-opacity`}>
            Check ticket
          </Link>
          {user ? (
            <Link to={user.role === "super_admin" ? "/admin" : "/dashboard"} className="gp-btn-teal rounded-full px-6 py-2.5 text-[0.95rem] font-medium">
              Dashboard
            </Link>
          ) : (
            <>
              <Link to="/login" className={`text-[0.95rem] font-medium ${textColor} hover:opacity-70 transition-opacity`}>
                Sign in
              </Link>
              <Cta className="gp-btn-teal rounded-full px-6 py-2.5 text-[0.95rem] font-medium" />
            </>
          )}
        </div>

        <button className={`lg:hidden ${textColor}`} onClick={() => setOpen(!open)}>
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-gray-100 px-6 py-4">
          <Link to="/events" data-testid="nav-browse-events-mobile" className="block py-3 text-[#16243b] font-medium border-b border-gray-50">Browse events</Link>
          {links.map((link, i) => (
            <a key={i} href={link.url || "#"} className="block py-3 text-[#16243b] font-medium border-b border-gray-50">
              {link.label}
            </a>
          ))}
          <Link to="/check-balance" className="block py-3 text-[#16243b] font-medium border-b border-gray-50">Check ticket</Link>
          {user ? (
            <Link to={user.role === "super_admin" ? "/admin" : "/dashboard"} className="gp-btn-teal rounded-full px-6 py-3 text-center font-medium mt-4 block">Dashboard</Link>
          ) : (
            <>
              <Link to="/login" className="block py-3 text-[#16243b] font-medium border-b border-gray-50">Sign in</Link>
              <Cta className="gp-btn-teal rounded-full px-6 py-3 text-center font-medium mt-4 block" />
            </>
          )}
        </div>
      )}
    </header>
  );
}
